// resources/js/components/bid_limits/BidLimitsCell.tsx

import * as React from 'react'
import { cents } from '../../util/formatters'
import { ResolvedBidLimits } from '../../types/bid_limits'
import { BidLimitsPopover } from './BidLimitsPopover'

/** Maps an AgGrid table's selectKey to the backend parent_type. */
const PARENT_TYPE_BY_KEY: Record<string, number> = {
    campaign_id: 1,
    ad_group_id: 2,
    keyword_id: 4,
    target_id: 5,
}

const emptyLimits = (): ResolvedBidLimits => ({
    min: { value: null, source: 'none' },
    max: { value: null, source: 'none' },
})

const Label = ({ limits }: { limits: ResolvedBidLimits | null }) => {
    const min = limits?.min
    const max = limits?.max
    const hasAny = (min?.value ?? null) !== null || (max?.value ?? null) !== null

    if (!hasAny) {
        return <span className="cursor-pointer text-gray-400 hover:text-blue-600">Set limits</span>
    }

    const fmt = (v: number | null | undefined, emptyText: string) =>
        v == null ? emptyText : cents(v)

    return (
        <span className="cursor-pointer">
            <span className={min?.source === 'self' ? 'text-gray-900' : 'text-gray-400'}>
                {fmt(min?.value, 'No min')}
            </span>
            <span className="mx-1 text-gray-300">–</span>
            <span className={max?.source === 'self' ? 'text-gray-900' : 'text-gray-400'}>
                {fmt(max?.value, 'No max')}
            </span>
        </span>
    )
}

/**
 * AgGrid cell renderer for the Bid Limits column. Reads `data.bid_limits`
 * (merged onto rows by the useBidLimits hook) and opens the editor popover
 * on click. Explicit ("self") values render solid; inherited values muted.
 */
export const BidLimitsCell = ({ data, context }: { data: any; context: any }) => {
    const keyField = context?.selectKey as string
    const parentType = PARENT_TYPE_BY_KEY[keyField]

    if (!parentType || data?.[keyField] == null) {
        return null
    }

    const parentId = String(data[keyField])
    const limits: ResolvedBidLimits | null | undefined = data.bid_limits

    // Not resolved yet (batch-resolve in flight) — neutral placeholder, no popover.
    if (limits === undefined) {
        return <span className="text-gray-300">—</span>
    }

    const onSaved = (next: ResolvedBidLimits) => {
        context.store.setItems((curr: any[]) =>
            curr.map((row) =>
                String(row[keyField]) === parentId ? { ...row, bid_limits: next } : row,
            ),
        )
    }

    return (
        <BidLimitsPopover
            parentType={parentType}
            parentId={parentId}
            limits={limits ?? emptyLimits()}
            onSaved={onSaved}
        >
            <Label limits={limits} />
        </BidLimitsPopover>
    )
}
